import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import swal from "sweetalert";
import {
  LoadingSpinner,
} from "../../../constants/const";
import { addMember } from "../../member/ApiMember";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { ApiDocuments } from "../../documents/ApiDocuments";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles, compressImageFile } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import { getMinMaxDates, validateTransactionDate } from "../fiscalYearHelper";

const getDateValue = (value) => {
  if (!value) return new Date();
  if (value instanceof Date) return value;

  const raw = String(value).trim();
  const parts = raw.split(/[-/]/).map(Number);

  if (parts.length === 3 && parts.every((part) => !Number.isNaN(part))) {
    if (String(parts[0]).length === 4) {
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }
    return new Date(parts[2], parts[1] - 1, parts[0]);
  }

  const parsedDate = new Date(raw);
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

const getAmount = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(Math.abs(Number(String(value).replace(/,/g, ""))));
};

const findOption = (options, values) =>
  options.find((option) => {
    const optionValues = [
      option?.value,
      option?.id,
      option?.label,
      option?.label1,
      option?.name,
      option?.title,
    ].map((item) => String(item || "").trim().toLowerCase());

    return values.some((value) =>
      optionValues.includes(String(value || "").trim().toLowerCase()),
    );
  }) || null;

const CustomInput = ({ label, value, onChange, error }) => (
  <FormGroup>
    <Label className="col-form-label">
      <GetLanguageString props={label} />
    </Label>
    <Input type="text" value={value} onChange={onChange} className="no-drag" />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

const renderWithUrduFont = (text) => {
  if (!text || typeof text !== "string") return text;
  const urduRegex = /[\u0600-\u06FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  if (!urduRegex.test(text)) return text;
  const chunkRegex = /([\u0600-\u06FF\uFB50-\uFDFF\uFE70-\uFEFF]+)/g;
  const parts = text.split(chunkRegex);
  return parts.map((part, idx) =>
    urduRegex.test(part) ? (
      <span key={idx} className="ur">
        {part}
      </span>
    ) : (
      part
    ),
  );
};

const EditOtherIncomeTarbiatiCourseReimbursment = ({
  setModal,
  transactionNo,
  transaction,
  unitId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [formData, setFormData] = useState({
    selectedFinanceCategory: transaction?.category || null,
    selectedAccount: null,
    date: getDateValue(transaction?.date),
    amount: getAmount(transaction?.amount),
    memo: transaction?.memo || "",
    reference: transaction?.reference || "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({
    bankAccounts: false,
    accounts: false,
    submit: false,
    attachments: false,
  });

  const [existingAttachments, setExistingAttachments] = useState(
    transaction?.driveFiles || transaction?.drive_files || transaction?.DriveFiles || []
  );
  const [expenseReceiptFile, setExpenseReceiptFile] = useState(null);
  const [incomeSummaryFile, setIncomeSummaryFile] = useState(null);

  const isBusy = Object.values(loading).some(Boolean);
  const toggle = () => setModal(false);

  const handleDrag = (e, ui) => {
    setPosition((prev) => ({
      x: prev.x + ui.deltaX,
      y: prev.y + ui.deltaY,
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  useEffect(() => {
    const transNo = transactionNo || transaction?.transactionNo || transaction?.transNo;
    if (transNo) {
      setLoading((prev) => ({ ...prev, attachments: true }));
      ApiFinanceLocal()
        .GetTransactionAttachments(transNo)
        .then((res) => {
          if (Array.isArray(res.data) && res.data.length > 0) {
            setExistingAttachments(res.data);
          }
        })
        .catch(console.error)
        .finally(() => setLoading((prev) => ({ ...prev, attachments: false })));
    }
  }, [transactionNo, transaction]);

  const existingExpense =
    existingAttachments.find(
      (f) =>
        (f.fileName || f.FileName || f.file_name || "").toLowerCase().includes("expense") ||
        (f.fileName || f.FileName || f.file_name || "").includes("اخراجات")
    ) || (existingAttachments.length > 0 ? existingAttachments[0] : null);

  const existingIncome =
    existingAttachments.find(
      (f) =>
        (f.fileName || f.FileName || f.file_name || "").toLowerCase().includes("income") ||
        (f.fileName || f.FileName || f.file_name || "").includes("آمدنی")
    ) || (existingAttachments.length > 1 ? existingAttachments[1] : null);

  const handleViewAttachment = async (fileItem) => {
    if (!fileItem) return;
    const webLink = fileItem.webViewLink || fileItem.WebViewLink || fileItem.web_view_link;
    const driveId = fileItem.driveFileId || fileItem.DriveFileId || fileItem.drive_file_id;
    if (webLink) {
      window.open(webLink, "_blank");
      return;
    }
    if (driveId) {
      try {
        const res = await ApiDocuments().downloadDocument(driveId);
        if (res.status === 200 && res.data?.downloadLink) {
          window.open(res.data.downloadLink, "_blank");
        } else {
          swal({ title: t(res.data) || "Unable to view file", icon: "error", buttons: "OK" });
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleExpenseReceiptChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 1500 * 1024; // 1.5 MB
    let processedFile = file;

    if (file.type && file.type.startsWith("image/") && file.size > MAX_SIZE) {
      try {
        processedFile = await compressImageFile(file, 1400 * 1024);
      } catch (err) {
        console.error("Image compression error:", err);
      }
    }

    if (processedFile.size > MAX_SIZE) {
      setExpenseReceiptFile(null);
      setErrors((prev) => ({
        ...prev,
        expenseReceiptFile: <GetLanguageString props="file_size_limit_1_5mb" />,
      }));
      e.target.value = "";
    } else {
      setExpenseReceiptFile(processedFile);
      setErrors((prev) => ({ ...prev, expenseReceiptFile: "" }));
    }
  };

  const handleIncomeSummaryChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 1500 * 1024; // 1.5 MB
    let processedFile = file;

    if (file.type && file.type.startsWith("image/") && file.size > MAX_SIZE) {
      try {
        processedFile = await compressImageFile(file, 1400 * 1024);
      } catch (err) {
        console.error("Image compression error:", err);
      }
    }

    if (processedFile.size > MAX_SIZE) {
      setIncomeSummaryFile(null);
      setErrors((prev) => ({
        ...prev,
        incomeSummaryFile: <GetLanguageString props="file_size_limit_1_5mb" />,
      }));
      e.target.value = "";
    } else {
      setIncomeSummaryFile(processedFile);
      setErrors((prev) => ({ ...prev, incomeSummaryFile: "" }));
    }
  };

  useEffect(() => {
    if (!unitId) return;

    setLoading((prev) => ({ ...prev, bankAccounts: true }));
    addMember()
      .GetUnitBankAccounts(unitId)
      .then((res) => {
        const list = res.data || [];
        setSelectedBankAccount(
          findOption(list, [
            transaction?.rafiqBankId,
            transaction?.bankAccountId,
            transaction?.bankName,
          ]) ||
            list[0] ||
            null,
        );
      })
      .catch((err) => console.error(err))
      .finally(() =>
        setLoading((prev) => ({ ...prev, bankAccounts: false })),
      );
  }, [transaction, unitId]);

  useEffect(() => {
    if (!transaction) return;
    const memoVal = transaction.memo || transaction.lineMemo || (Array.isArray(transaction.details) && transaction.details[0]?.memo) || (Array.isArray(transaction.details) && transaction.details[0]?.lineMemo) || "";
    setFormData((prev) => ({
      ...prev,
      selectedFinanceCategory: transaction.category || prev.selectedFinanceCategory,
      date: getDateValue(transaction.date),
      amount: getAmount(transaction.amount),
      memo: memoVal,
      reference: transaction.reference || "",
    }));
  }, [transaction]);

  useEffect(() => {
    const categoryId = formData.selectedFinanceCategory?.value;
    if (!categoryId) return;

    setLoading((prev) => ({ ...prev, accounts: true }));
    ApiFinance()
      .GetFinanceSubCategoryAccountsById(categoryId)
      .then((res) => {
        const accounts = res.data || [];
        setFormData((prev) => ({
          ...prev,
          selectedAccount:
            findOption(accounts, [transaction?.codeId, transaction?.accountName]) ||
            accounts[0] ||
            null,
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to load income heads.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, accounts: false })));
  }, [formData.selectedFinanceCategory, transaction]);

  const validate = () => {
    const nextErrors = {
      amount: formData.amount ? (
        ""
      ) : (
        <GetLanguageString props="funds_add_fund_amount_paid_error" />
      ),
    };

    const dateError = validateTransactionDate(formData.date, transaction?.date);
    if (dateError) {
      nextErrors.date = t(dateError);
    }

    const maxSize = 1500 * 1024;
    if (!existingExpense && !expenseReceiptFile) {
      nextErrors.expenseReceiptFile = <GetLanguageString props="expense_receipts_required" />;
    } else if (expenseReceiptFile && expenseReceiptFile.size > maxSize) {
      nextErrors.expenseReceiptFile = <GetLanguageString props="file_size_limit_1_5mb" />;
    }

    if (!existingIncome && !incomeSummaryFile) {
      nextErrors.incomeSummaryFile = <GetLanguageString props="income_summary_required" />;
    } else if (incomeSummaryFile && incomeSummaryFile.size > maxSize) {
      nextErrors.incomeSummaryFile = <GetLanguageString props="file_size_limit_1_5mb" />;
    }

    setErrors(nextErrors);
    return Object.values(nextErrors).every((value) => value === "");
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validate()) return;

    const payload = new FormData();
    payload.append("transactionNo", transactionNo || transaction?.transactionId || transaction?.transNo || 0);
    payload.append("rafiqBankId", selectedBankAccount?.value ?? 0);
    payload.append(
      "date",
      formData.date
        ? (formData.date instanceof Date ? formData.date.toISOString() : new Date(formData.date).toISOString())
        : new Date().toISOString()
    );
    payload.append("amount", formData.amount);
    payload.append("memo", formData.memo);
    payload.append("reference", formData.reference || "");
    payload.append("unitId", unitId || transaction?.unitId || 0);

    if (formData.selectedAccount?.value) {
      payload.append("financeAccount.value", formData.selectedAccount.value);
    }
    if (formData.selectedFinanceCategory?.label1) {
      payload.append("category.label1", formData.selectedFinanceCategory.label1);
    }
    if (formData.selectedFinanceCategory?.value) {
      payload.append("category.value", formData.selectedFinanceCategory.value);
    }

    if (expenseReceiptFile) {
      payload.append("expenseReceiptFile", expenseReceiptFile);
    }
    if (incomeSummaryFile) {
      payload.append("incomeSummaryFile", incomeSummaryFile);
    }

    const kept = [];
    if (!expenseReceiptFile && existingExpense) kept.push(existingExpense);
    if (!incomeSummaryFile && existingIncome) kept.push(existingIncome);
    if (kept.length > 0) {
      kept.forEach((item, index) => {
        const dId = item.driveFileId || item.DriveFileId || item.drive_file_id;
        const fName = item.fileName || item.FileName || item.file_name;
        const wLink = item.webViewLink || item.WebViewLink || item.web_view_link;
        if (dId) payload.append(`keptDriveFiles[${index}].driveFileId`, dId);
        if (fName) payload.append(`keptDriveFiles[${index}].fileName`, fName);
        if (wLink) payload.append(`keptDriveFiles[${index}].webViewLink`, wLink);
      });
    }

    setLoading((prev) => ({ ...prev, submit: true }));
    ApiFinanceLocal()
      .RequestModificationOtherIncomeTarbiatiCourseReimbursment(payload)
      .then((res) => {
        const data = res.data || {};
        if (Number(data.statusCode) !== 200) {
          const isWarning = Number(data.statusCode) === 300 || data.message === "modification_request_already_pending";
          swal({
            title: isWarning ? t("common_warning") || "Warning" : "Error",
            text: t(data.message) || data.message || "Failed to update Other Income",
            icon: isWarning ? "warning" : "error",
            buttons: "OK",
          }).then(() => {
            if (isWarning) {
              setModal(false);
            }
          });
          return;
        }

        swal({
          title: "Success",
          text: t(data.message) || t("member_member_detail_fund_edited"),
          icon: "success",
          buttons: "OK",
        }).then(() => {
          setModal(false);
          if (typeof onSuccess === "function") onSuccess();
        });
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to update Other Income.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, submit: false })));
  };

  return (
    <Draggable position={position} onDrag={handleDrag} handle=".modal-header" cancel=".no-drag">
      <Modal isOpen className="inmodal" size="lg" autoFocus={false}>
        <ModalHeader className="modal-title" tag="h4">
          <GetLanguageString props="funds_other_income_tarbiati_course_reimbursment" />
        </ModalHeader>

        {isBusy ? (
          <ModalBody className="text-center">
            <LoadingSpinner />
          </ModalBody>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            <ModalBody>
              <Row>
                <Col md="12" className="mb-3">
                  <div style={{ background: "#FFF8E1", border: "1px solid #FFD54F", borderRadius: "8px", padding: "12px 16px", display: "flex", alignItems: "center", gap: "10px" }}>
                    <i className="fa fa-info-circle" style={{ color: "#F57F17", fontSize: "18px" }}></i>
                    <span style={{ fontSize: "13px", color: "#5D4037" }}>
                      {t("amanat_edit_modification_notice")}
                    </span>
                  </div>
                </Col>

                <Col md="4">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="funds_date" />
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      minDate={getMinMaxDates(formData.date, transaction?.date).minDate}
                      maxDate={getMinMaxDates(formData.date, transaction?.date).maxDate}
                      onChange={(date) =>
                        handleChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                    {errors.date && (
                      <h4 className="text-error">{errors.date}</h4>
                    )}
                  </FormGroup>
                </Col>
                <Col md="4">
                  <CustomInput
                    label="funds_add_fund_amount"
                    value={formData.amount}
                    onChange={(e) => handleChange("amount", e.target.value)}
                    error={errors.amount}
                  />
                </Col>
                <Col md="4">
                  <CustomInput
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>
              </Row>

              {/* Attachments (Expense Receipts & Income Summary) */}
              <Row className="mt-2">
                {/* Expense Receipts */}
                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="expense_receipts_attachment" />
                      {!existingExpense && <span className="text-danger">*</span>}
                    </Label>

                    {existingExpense && (
                      <div
                        className="d-flex justify-content-between align-items-center p-2 mb-2 rounded border bg-light"
                        style={{ borderColor: "#E5E7EB" }}
                      >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: "65%" }}>
                          <i className="fa fa-file text-muted"></i>
                          <div className="text-truncate">
                            <div className="text-truncate" style={{ fontSize: "12px", fontWeight: "600", color: "#374151" }}>
                              {renderWithUrduFont(existingExpense.fileName || existingExpense.FileName || existingExpense.file_name || "Expense Receipt")}
                            </div>
                            <span className="badge bg-info text-white" style={{ fontSize: "10px" }}>
                              <GetLanguageString props="tarbiati_course_existing_file" />
                            </span>
                          </div>
                        </div>
                        <Button
                          color="white"
                          size="sm"
                          style={{
                            padding: "4px 10px",
                            fontSize: "11px",
                            fontWeight: "500",
                            borderRadius: "6px",
                            border: "1px solid #D1D5DB",
                          }}
                          onClick={() => handleViewAttachment(existingExpense)}
                          type="button"
                        >
                          <i className="fa fa-eye text-primary"></i>&nbsp;
                          <GetLanguageString props="member_add_rafiq_request_view_document" />
                        </Button>
                      </div>
                    )}

                    <Input
                      type="file"
                      accept="image/*,application/pdf"
                      name="expenseReceiptFile"
                      className="form-control"
                      onChange={handleExpenseReceiptChange}
                      invalid={!!errors.expenseReceiptFile}
                    />
                    {errors.expenseReceiptFile && (
                      <h4 className="text-error mt-1">{errors.expenseReceiptFile}</h4>
                    )}

                    {expenseReceiptFile && (
                      <div
                        className="d-flex align-items-center justify-content-between p-2 mt-2 rounded"
                        style={{
                          backgroundColor: "#ECFDF5",
                          border: "1px solid #A7F3D0",
                          fontSize: "12px",
                          color: "#065F46",
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: "80%" }}>
                          <i className="fa fa-file text-success"></i>
                          <span className="text-truncate fw-bold">{expenseReceiptFile.name}</span>
                          <span className="badge bg-light text-muted border px-1">
                            {(expenseReceiptFile.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        <Button
                          color="link"
                          size="sm"
                          className="text-danger p-0 ms-2 text-decoration-none"
                          onClick={() => setExpenseReceiptFile(null)}
                          type="button"
                        >
                          <i className="fa fa-times"></i>
                        </Button>
                      </div>
                    )}
                  </FormGroup>
                </Col>

                {/* Income Summary */}
                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="income_summary_attachment" />
                      {!existingIncome && <span className="text-danger">*</span>}
                    </Label>

                    {existingIncome && (
                      <div
                        className="d-flex justify-content-between align-items-center p-2 mb-2 rounded border bg-light"
                        style={{ borderColor: "#E5E7EB" }}
                      >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: "65%" }}>
                          <i className="fa fa-file text-muted"></i>
                          <div className="text-truncate">
                            <div className="text-truncate" style={{ fontSize: "12px", fontWeight: "600", color: "#374151" }}>
                              {renderWithUrduFont(existingIncome.fileName || existingIncome.FileName || existingIncome.file_name || "Income Summary")}
                            </div>
                            <span className="badge bg-info text-white" style={{ fontSize: "10px" }}>
                              <GetLanguageString props="tarbiati_course_existing_file" />
                            </span>
                          </div>
                        </div>
                        <Button
                          color="white"
                          size="sm"
                          style={{
                            padding: "4px 10px",
                            fontSize: "11px",
                            fontWeight: "500",
                            borderRadius: "6px",
                            border: "1px solid #D1D5DB",
                          }}
                          onClick={() => handleViewAttachment(existingIncome)}
                          type="button"
                        >
                          <i className="fa fa-eye text-primary"></i>&nbsp;
                          <GetLanguageString props="member_add_rafiq_request_view_document" />
                        </Button>
                      </div>
                    )}

                    <Input
                      type="file"
                      accept="image/*,application/pdf"
                      name="incomeSummaryFile"
                      className="form-control"
                      onChange={handleIncomeSummaryChange}
                      invalid={!!errors.incomeSummaryFile}
                    />
                    {errors.incomeSummaryFile && (
                      <h4 className="text-error mt-1">{errors.incomeSummaryFile}</h4>
                    )}

                    {incomeSummaryFile && (
                      <div
                        className="d-flex align-items-center justify-content-between p-2 mt-2 rounded"
                        style={{
                          backgroundColor: "#ECFDF5",
                          border: "1px solid #A7F3D0",
                          fontSize: "12px",
                          color: "#065F46",
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: "80%" }}>
                          <i className="fa fa-file text-success"></i>
                          <span className="text-truncate fw-bold">{incomeSummaryFile.name}</span>
                          <span className="badge bg-light text-muted border px-1">
                            {(incomeSummaryFile.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        <Button
                          color="link"
                          size="sm"
                          className="text-danger p-0 ms-2 text-decoration-none"
                          onClick={() => setIncomeSummaryFile(null)}
                          type="button"
                        >
                          <i className="fa fa-times"></i>
                        </Button>
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>

            <ModalFooter>
              <Button
                color="white"
                className={GetCustomStyles().btn_style}
                onClick={toggle}
              >
                <GetLanguageString props="common_cancel" />
              </Button>
              <Button
                color="warning"
                className={GetCustomStyles().btn_style}
                disabled={isBusy}
                type="submit"
              >
                <GetLanguageString props="submit_modification_request" />
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Modal>
    </Draggable>
  );
};

export default EditOtherIncomeTarbiatiCourseReimbursment;
